import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

// ══════════════════════════════════════════════
//  SessionService — Fogón v9
//  Servicio reactivo que gestiona la sesión del
//  usuario y el local activo. Todos los módulos
//  se suscriben a los observables para recibir
//  cambios en tiempo real.
// ══════════════════════════════════════════════

export interface ISessionLocal {
  idRoom: string;
  description: string;
  address?: string;
}

/** Premise (Local/Sucursal) — según DADS */
export interface ISessionPremise {
  idPremise: number;
  description: string;
  address?: string;
  phone?: string;
  state?: number;
  ipCashbox?: string;
  // idRoom asignado al usuario PARA ESTE local. Cada premise puede
  // tener un salón distinto por usuario (multi-asignación).
  idRoom?: number | string;
}

/** Company — según DADS */
export interface ISessionCompany {
  idCompany: string;
  businessName: string;
  ruc?: string;
}

export interface ISessionUser {
  idUser: string;
  userName: string;
  name: string;
  lastName: string;
  idRole: string;
  token: string;
  state: string;
}

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  // ─── User session ─────────────────────────
  private _user = new BehaviorSubject<ISessionUser | null>(null);
  user$: Observable<ISessionUser | null> = this._user.asObservable();

  // ─── Active local ─────────────────────────
  private _activeLocal = new BehaviorSubject<ISessionLocal | null>(null);
  activeLocal$: Observable<ISessionLocal | null> = this._activeLocal.asObservable();

  // ─── Available locals ─────────────────────
  private _locals = new BehaviorSubject<ISessionLocal[]>([]);
  locals$: Observable<ISessionLocal[]> = this._locals.asObservable();

  // ─── Company name ─────────────────────────
  private _companyName = new BehaviorSubject<string>('');
  companyName$: Observable<string> = this._companyName.asObservable();

  // ─── Company object ───────────────────────
  private _company = new BehaviorSubject<ISessionCompany | null>(null);
  company$: Observable<ISessionCompany | null> = this._company.asObservable();

  // ─── Premises (Locales/Sucursales) ────────
  private _premises = new BehaviorSubject<ISessionPremise[]>([]);
  premises$: Observable<ISessionPremise[]> = this._premises.asObservable();

  // ─── Active premise ───────────────────────
  private _activePremise = new BehaviorSubject<ISessionPremise | null>(null);
  activePremise$: Observable<ISessionPremise | null> = this._activePremise.asObservable();

  // ─── User-premise assignments (multi-room) ──
  // Cada entrada del array original del login: { idPremise, idRoom }.
  // Un mismo idPremise puede aparecer N veces con distinto idRoom.
  // Esto alimenta la dinámica de TABS por salón asignado.
  private _userPremiseRooms = new BehaviorSubject<Array<{ idPremise: number | string, idRoom: number | string }>>([]);
  userPremiseRooms$: Observable<Array<{ idPremise: number | string, idRoom: number | string }>> = this._userPremiseRooms.asObservable();

  constructor() {
    this.restoreFromStorage();
  }

  // ════════════════════════════════════════════
  //  Restore session from sessionStorage
  // ════════════════════════════════════════════
  restoreFromStorage(): void {
    const isLoggedIn = sessionStorage.getItem('isLoggedinFogon');
    if (!isLoggedIn) return;

    // Restore user
    const user: ISessionUser = {
      idUser: sessionStorage.getItem('idUser') || '',
      userName: sessionStorage.getItem('userName') || '',
      name: sessionStorage.getItem('name') || '',
      lastName: sessionStorage.getItem('lastName') || '',
      idRole: sessionStorage.getItem('idRole') || '',
      token: sessionStorage.getItem('token') || '',
      state: sessionStorage.getItem('state') || '1',
    };
    this._user.next(user);

    // Restore active local
    const storedLocal = sessionStorage.getItem('fg_activeLocal');
    if (storedLocal) {
      try {
        const local: ISessionLocal = JSON.parse(storedLocal);
        this._activeLocal.next(local);
      } catch (_) {
        // Fallback: build from idRoom
        this._setLocalFromIdRoom(sessionStorage.getItem('idRoom') || '');
      }
    } else {
      this._setLocalFromIdRoom(sessionStorage.getItem('idRoom') || '');
    }

    // Restore locals list
    const storedLocals = sessionStorage.getItem('fg_locals');
    if (storedLocals) {
      try {
        this._locals.next(JSON.parse(storedLocals));
      } catch (_) { }
    }

    // Restore company name
    const company = sessionStorage.getItem('fg_companyName') || '';
    this._companyName.next(company);

    // Restore company object
    const storedCompany = sessionStorage.getItem('fg_company');
    if (storedCompany) {
      try { this._company.next(JSON.parse(storedCompany)); } catch (_) { }
    }

    // Restore premises
    const storedPremises = sessionStorage.getItem('fg_premises');
    if (storedPremises) {
      try { this._premises.next(JSON.parse(storedPremises)); } catch (_) { }
    }

    // Restore raw user-premise rows (multi-room por premise)
    const storedRows = sessionStorage.getItem('fg_userPremiseRooms');
    if (storedRows) {
      try { this._userPremiseRooms.next(JSON.parse(storedRows)); } catch (_) { }
    }

    // Restore active premise
    const storedActivePremise = sessionStorage.getItem('fg_activePremise');
    if (storedActivePremise) {
      try {
        const parsed = JSON.parse(storedActivePremise);
        this._activePremise.next(parsed);
        // Si el premise restaurado trae idRoom y sessionStorage.idRoom
        // está vacío o quedó con sentinela "-1", lo sincronizamos.
        // Esto cubre el caso del refresh tras login viejo.
        const currentIdRoom = sessionStorage.getItem('idRoom');
        if (parsed && parsed.idRoom != null && parsed.idRoom !== ''
            && (!currentIdRoom || currentIdRoom === '-1' || currentIdRoom === '')) {
          sessionStorage.setItem('idRoom', String(parsed.idRoom));
        }
      } catch (_) { }
    }
  }

  // ════════════════════════════════════════════
  //  Set user session (called after login)
  // ════════════════════════════════════════════
  setUser(user: ISessionUser): void {
    this._user.next(user);
    sessionStorage.setItem('idUser', user.idUser);
    sessionStorage.setItem('userName', user.userName);
    sessionStorage.setItem('name', user.name);
    sessionStorage.setItem('lastName', user.lastName);
    sessionStorage.setItem('idRole', user.idRole);
    sessionStorage.setItem('token', user.token);
    sessionStorage.setItem('state', user.state);
  }

  // ════════════════════════════════════════════
  //  Set available locals (from API)
  // ════════════════════════════════════════════
  setLocals(locals: ISessionLocal[]): void {
    this._locals.next(locals);
    sessionStorage.setItem('fg_locals', JSON.stringify(locals));
  }

  // ════════════════════════════════════════════
  //  Switch active local
  // ════════════════════════════════════════════
  switchLocal(local: ISessionLocal): void {
    sessionStorage.setItem('fg_activeLocal', JSON.stringify(local));
    sessionStorage.setItem('idRoom', local.idRoom);
    this._activeLocal.next(local);
  }

  // ════════════════════════════════════════════
  //  Set company name
  // ════════════════════════════════════════════
  setCompanyName(name: string): void {
    this._companyName.next(name);
    sessionStorage.setItem('fg_companyName', name);
  }

  // ════════════════════════════════════════════
  //  Set company object (from login response)
  // ════════════════════════════════════════════
  setCompany(company: ISessionCompany): void {
    sessionStorage.setItem('fg_company', JSON.stringify(company));
    sessionStorage.setItem('idCompany', company.idCompany);
    if (company.businessName) {
      this.setCompanyName(company.businessName);
    }
    this._company.next(company);
  }

  // ════════════════════════════════════════════
  //  Set premises (from login response)
  // ════════════════════════════════════════════
  setPremises(premises: ISessionPremise[]): void {
    this._premises.next(premises);
    sessionStorage.setItem('fg_premises', JSON.stringify(premises));
  }

  // ════════════════════════════════════════════
  //  Set raw user-premise pairs ({idPremise, idRoom}[])
  //  El login devuelve un par por cada asignación; un mismo
  //  idPremise puede repetirse con distintos idRoom.
  // ════════════════════════════════════════════
  setUserPremiseRooms(rows: Array<{ idPremise: number | string, idRoom: number | string }>): void {
    this._userPremiseRooms.next(rows);
    sessionStorage.setItem('fg_userPremiseRooms', JSON.stringify(rows));
  }

  /** Salones asignados al usuario para el premise activo (array de idRoom). */
  getRoomsForPremise(idPremise: number | string): Array<number | string> {
    const rows = this._userPremiseRooms.getValue() || [];
    const target = Number(idPremise);
    return rows
      .filter(r => Number(r.idPremise) === target)
      .map(r => r.idRoom);
  }

  // ════════════════════════════════════════════
  //  Switch active premise
  // ════════════════════════════════════════════
  switchPremise(premise: ISessionPremise): void {
    // Actualizar sessionStorage ANTES de emitir, porque los subscribers
    // usan getActivePremiseId() que lee de sessionStorage sincrónicamente
    sessionStorage.setItem('fg_activePremise', JSON.stringify(premise));
    sessionStorage.setItem('idPremise', String(premise.idPremise));
    // Sincronizar idRoom del usuario para ESTE local. Cada premise
    // puede tener un idRoom distinto asignado para el mismo usuario
    // (multi-asignación). Esto permite que el MOZO vea sólo el salón
    // que le toca por local + su PARA LLEVAR.
    if (premise.idRoom != null && premise.idRoom !== '') {
      sessionStorage.setItem('idRoom', String(premise.idRoom));
    }
    // Persistir el último local elegido entre sesiones (sobrevive al logout)
    try { localStorage.setItem('fg_lastPremiseId', String(premise.idPremise)); } catch (_) { }
    this._activePremise.next(premise);
  }

  // ════════════════════════════════════════════
  //  Getters (snapshot, non-reactive)
  // ════════════════════════════════════════════
  get currentUser(): ISessionUser | null {
    return this._user.getValue();
  }

  get currentLocal(): ISessionLocal | null {
    return this._activeLocal.getValue();
  }

  get currentLocals(): ISessionLocal[] {
    return this._locals.getValue();
  }

  get currentCompanyName(): string {
    return this._companyName.getValue();
  }

  get currentCompany(): ISessionCompany | null {
    return this._company.getValue();
  }

  get currentPremises(): ISessionPremise[] {
    return this._premises.getValue();
  }

  get currentPremise(): ISessionPremise | null {
    return this._activePremise.getValue();
  }

  // ════════════════════════════════════════════
  //  Clear session (logout)
  // ════════════════════════════════════════════
  clear(): void {
    // Preservar clienteId para redirigir al login con ?cliente=
    const clienteId = sessionStorage.getItem('clienteId') || '';
    this._user.next(null);
    this._activeLocal.next(null);
    this._locals.next([]);
    this._companyName.next('');
    sessionStorage.clear();
    if (clienteId) {
      sessionStorage.setItem('clienteId', clienteId);
    }
  }

  // ─── private helpers ───────────────────────
  private _setLocalFromIdRoom(idRoom: string): void {
    if (idRoom) {
      const local: ISessionLocal = {
        idRoom: idRoom,
        description: `Local ${idRoom}`, // Placeholder hasta que API devuelva nombre real
      };
      this._activeLocal.next(local);
      sessionStorage.setItem('fg_activeLocal', JSON.stringify(local));
    }
  }
}
